// Pure colour maths for the theme's contrast decisions — no Mantine, no DOM. Everything works in
// gamma-encoded sRGB, because that is the space browsers composite alpha in and the space
// `color-mix(in srgb, …)` interpolates in, so the numbers here are the numbers the page renders.

type Rgb = readonly [number, number, number];

/** WCAG 2.x AA for body-size text. */
export const minimumTextContrast = 4.5;

function hexToRgb(hex: string): Rgb {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match?.[1]) throw new Error(`Expected a #rrggbb colour, got "${hex}"`);
  const value = Number.parseInt(match[1], 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

function channelLuminance(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance([r, g, b]: Rgb): number {
  return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
}

function ratio(a: Rgb, b: Rgb): number {
  const [lighter, darker] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return ((lighter ?? 0) + 0.05) / ((darker ?? 0) + 0.05);
}

/** `amount` of `top` over `bottom`, per channel — alpha compositing and `color-mix` alike. */
function mix(bottom: Rgb, top: Rgb, amount: number): Rgb {
  const at = (i: 0 | 1 | 2) => bottom[i] * (1 - amount) + top[i] * amount;
  return [at(0), at(1), at(2)];
}

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];

/** WCAG contrast ratio between two `#rrggbb` colours. */
export function contrastRatio(foreground: string, background: string): number {
  return ratio(hexToRgb(foreground), hexToRgb(background));
}

/**
 * How much black to mix into `ink` (as a whole percentage, in 5% steps) so it reads at
 * `minimumTextContrast` on Mantine's light-variant ground: `tint` at `tintAlpha` over a white
 * body. `0` when it already passes. This is what the theme's `variantColorResolver` applies to
 * tinted Badge and Alert text in the light scheme.
 */
export function inkDarkening(ink: string, tint: string, tintAlpha = 0.1): number {
  const inkRgb = hexToRgb(ink);
  const ground = mix(WHITE, hexToRgb(tint), tintAlpha);
  for (let percent = 0; percent <= 100; percent += 5) {
    if (ratio(mix(inkRgb, BLACK, percent / 100), ground) >= minimumTextContrast) return percent;
  }
  return 100;
}
